/**
 * Central constants for the cruise module (CRUISEGATEWAY on v2dispatch).
 *
 * Every route, action code and limit the module depends on lives here, so
 * this is the one file to audit when the module is copied to another client.
 */

// Search lives on the landing page as the "Cruises" tab.
export const CRUISE_ROUTES = {
  LANDING: '/?searchtype=cruise',
  RESULTS: '/cruise/results',
  DETAILS: '/cruise/details',
  CABIN: '/cruise/cabin',
  GUESTS: '/cruise/guests',
  CONFIRMATION: '/cruise/confirmation',
};

export const CRUISE_OPID = 'CRUISEGATEWAY';

/** How many months ahead the sailing-month dropdown offers. */
export const SAILING_MONTHS_AHEAD = 24;

export const CRUISE_ACTIONS = {
  DESTINATIONS: 'DESTINATIONS',
  SEARCH: 'CRUISESEARCH',
  // The backend spells it this way.
  ITINERARY: 'CRUISEITINARY',
  CATEGORIES: 'CRUISECATEGORIES',
  CABINS: 'CRUISECABINS',
  PRICE_CHECK: 'CRUISEPRICECHECK',
  BOOK: 'CRUISEBOOK',
};

/** The progress bar shown on every page after search. */
export const CRUISE_STEPS = [
  { key: 'search', label: 'Search', route: CRUISE_ROUTES.LANDING },
  { key: 'results', label: 'Select cruise', route: CRUISE_ROUTES.RESULTS },
  { key: 'details', label: 'Cabin category', route: CRUISE_ROUTES.DETAILS },
  { key: 'cabin', label: 'Cabin & bed', route: CRUISE_ROUTES.CABIN },
  { key: 'guests', label: 'Guest details', route: CRUISE_ROUTES.GUESTS },
  { key: 'done', label: 'Confirmation', route: CRUISE_ROUTES.CONFIRMATION },
];

/**
 * Occupancy and age rules of the cruise line on CRUISEGATEWAY today
 * (Holland America, as published on STIC). The search API takes ages but
 * does not return the line's rules, so they live here — the one place to
 * change when another line is added.
 */

/** Guests in one stateroom, infants included (HAL largest cabins: 4). */
export const MAX_GUESTS_PER_CABIN = 4;

export const CRUISE_GUEST_LIMITS = {
  adults: { min: 1, max: MAX_GUESTS_PER_CABIN },
  children: { min: 0, max: MAX_GUESTS_PER_CABIN - 1 },
  infants: { min: 0, max: 2 },
};

/**
 * Age on the sailing date each guest type must have. Adults and children
 * are in whole years; infants in months (HAL: 6–23 months).
 */
export const CRUISE_AGE_LIMITS = {
  adult: { min: 18, max: 120, unit: 'years' },
  child: { min: 2, max: 17, unit: 'years' },
  infant: { min: 6, max: 23, unit: 'months' },
};

export const GUEST_TYPE_HINTS = {
  adults: '18+ years',
  children: '2–17 years',
  infants: '6–23 months',
};

export const CHILD_AGE_OPTIONS = Array.from(
  { length: CRUISE_AGE_LIMITS.child.max - CRUISE_AGE_LIMITS.child.min + 1 },
  (_, i) => ({
    value: i + CRUISE_AGE_LIMITS.child.min,
    label: String(i + CRUISE_AGE_LIMITS.child.min),
  }),
);

/** Infant ages go to the API in whole years (0 or 1). */
export const INFANT_AGE_OPTIONS = [
  { value: 0, label: '6–11 months' },
  { value: 1, label: '12–23 months' },
];

export const DEFAULT_CHILD_AGE = 8;
export const DEFAULT_INFANT_AGE = 1;

/** Cabin `meta` codes returned by CRUISECATEGORIES / CRUISESEARCH. */
export const CABIN_META = {
  I: 'Interior',
  O: 'Ocean-view',
  B: 'Verandah',
  S: 'Suite',
};
export const CABIN_META_ORDER = ['I', 'O', 'B', 'S'];

export const FARE_TYPES = {
  BESTFARE: { label: 'Best Fare', sub: 'Lowest price' },
  BESTVALUE: { label: 'Best Value', sub: 'Includes perks' },
};

/**
 * `categoryStatus` on each CRUISECATEGORIES item (and on each search
 * fareOption): G = guarantee, Y = available. Worded for agents per QA.
 */
export const CATEGORY_STATUS = {
  G: {
    label: 'We choose your Stateroom',
    tone: 'warning',
    tip: 'Guarantee: the cruise line assigns a stateroom in this category (or better) closer to sailing. The cabin number is not chosen now.',
  },
  Y: {
    label: 'You choose your Stateroom',
    tone: 'success',
    tip: 'Available: pick the exact stateroom (deck and location) on the next step.',
  },
};

export const GUARANTEED_CABIN = 'GUAR';

export const DURATION_BUCKETS = [
  { key: 'short', label: 'Up to 7 nights', test: (n) => n <= 7 },
  { key: 'mid', label: '8 – 12 nights', test: (n) => n > 7 && n <= 12 },
  { key: 'long', label: '13+ nights', test: (n) => n > 12 },
];

export const SORT_OPTIONS = [
  { key: 'price', label: 'Price: low to high' },
  { key: 'priceDesc', label: 'Price: high to low' },
  { key: 'date', label: 'Departure date' },
  { key: 'duration', label: 'Duration' },
];

export const TITLE_OPTIONS = {
  adult: [
    { value: 'MR', label: 'Mr' },
    { value: 'MRS', label: 'Mrs' },
    { value: 'MS', label: 'Ms' },
  ],
  child: [
    { value: 'MSTR', label: 'Master' },
    { value: 'MISS', label: 'Miss' },
  ],
};
TITLE_OPTIONS.infant = TITLE_OPTIONS.child;

export const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

/** Title -> gender, so picking a title fills gender in. */
export const TITLE_GENDER = {
  MR: 'M',
  MSTR: 'M',
  MRS: 'F',
  MS: 'F',
  MISS: 'F',
};

/**
 * Same mode codes the bus and hotel flows send as `paymentmode`.
 * cashcard is the agent's wallet; every other mode goes through a gateway.
 */
/** Time to finish a booking after the price check (QA: 10 minutes). */
export const BOOKING_TIME_LIMIT_MS = 10 * 60 * 1000;

export const WALLET_MODE = 'cashcard';

/**
 * Display text for payment modes. Which modes are offered comes from the
 * agent's available gateways (GETAVAILABLEPG); this only names them.
 */
export const PAYMENT_MODE_TEXT = {
  [WALLET_MODE]: { label: 'Pay from wallet', sub: '' },
  creditcard: { label: 'Credit card', sub: 'Visa, Mastercard, RuPay, Amex' },
  debitcard: { label: 'Debit card', sub: 'All major Indian bank debit cards' },
  upi: { label: 'UPI', sub: 'GPay, PhonePe, Paytm & more' },
};

/**
 * Gateway modes the cruise page does not offer: net banking needs a bank
 * picker the book call has no field for yet.
 */
export const UNSUPPORTED_GATEWAY_MODES = ['netbanking', WALLET_MODE];

/** paymentSchedule codes in the CRUISEPRICECHECK invoice. */
export const PAYMENT_CODE_AGENCY = 'A';

/** cancellationPenalties.penaltyType: P = percent of fare, A = flat amount. */
export const PENALTY_PERCENT = 'P';

export const NAME_REGEX = /^[a-zA-Z ]*$/;
export const PASSPORT_REGEX = /^[A-Z0-9]{6,12}$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const MOBILE_REGEX = /^\d{7,15}$/;
export const PIN_REGEX = /^[A-Za-z0-9 -]{3,10}$/;
